import React, { useImperativeHandle, useRef, useMemo } from "react";
import { TransitionGroup } from "react-transition-group";
import { useSnackbar } from "notistack";
import { Grid } from "@mui/material";
import useNonMember from "@icoach/nonMember/components/useNonMember";
import { MASK_TYPE, Icons, Button, TextField, Typography, IconButton, Collapse, SelectField, MenuItem } from "@curves/ui-base";
import { isEqual, refIsRequiredError } from "@util/utils";
import { PEOPLE_SOURCE_TYPE } from "@icoach/constants/";
import { FRIEND_ORIGIN_RULE } from "@icoach/constants/appConfig";

// 友人資訊
const GourpFriends = React.forwardRef((props, ref) => {
	const { onRemoveFriendsClick, options = [] } = props;
	const { getOriginType } = useNonMember();
	const nameRef = useRef(null);
	const phoneRef = useRef(null);
	const originRef = useRef(null);

	useImperativeHandle(
		ref,
		() => ({
			isError: () => refIsRequiredError(nameRef, phoneRef, originRef),
			getResult: () => {
				return {
					name: nameRef.current.getResult(),
					cellphone: phoneRef.current.getResult(),
					sourceType: originRef.current.getResult(),
				};
			},
		}),
		// eslint-disable-next-line
		[]
	);

	const lockOriginValue = useMemo(
		() => {
			const value = getOriginType();
			return FRIEND_ORIGIN_RULE.some(x => isEqual(value, x)) ? value : PEOPLE_SOURCE_TYPE.BRLIST;
		},
		// eslint-disable-next-line
		[]
	);

	return (
		<Grid container spacing={1}>
			<Grid item xs={3}>
				<TextField label="姓名" ref={nameRef} required />
			</Grid>
			<Grid item xs={4}>
				<TextField label="手機" ref={phoneRef} maskType={MASK_TYPE.MOB} required />
			</Grid>
			<Grid item xs={4}>
				<SelectField ref={originRef} label="來源" defaultValue={lockOriginValue} displayEmpty disabled required>
					<MenuItem disabled value="">
						<em>請選擇</em>
					</MenuItem>
					{options.map(({ text, value, disabled }) => (
						<MenuItem value={value} key={value} disabled={disabled}>
							{text}
						</MenuItem>
					))}
				</SelectField>
			</Grid>
			<Grid item xs={1} style={{ margin: "22px 0 0 -20px" }}>
				<IconButton onClick={onRemoveFriendsClick}>
					<Icons.ErrorCircle />
				</IconButton>
			</Grid>
		</Grid>
	);
});

const InviteFriendsBox = React.forwardRef((props, ref) => {
	const { buttonLabel = "友人邀約", options } = props;
	const { getOriginType } = useNonMember();
	const { enqueueSnackbar } = useSnackbar();
	const [isFriends, setFriends] = React.useState(0);
	const [deleteIdx, setDeleteIdx] = React.useState([]);
	const friendsRef = useRef([]);

	const handleAddFriendsClick = e => {
		if (getOriginType && getOriginType()) {
			setFriends(isFriends + 1);
		} else {
			enqueueSnackbar("請選擇來源", { variant: "info" });
		}
	};

	const handleRemoveFriendsClick = id => e => {
		let newState = [];
		if (id !== "all") newState = [...deleteIdx, id];
		if (isFriends !== 0 && isFriends === deleteIdx.length + 1) newState = [];
		if (newState.length === 0) {
			setFriends(0);
			friendsRef.current = [];
		}
		setDeleteIdx([...newState]);
	};

	useImperativeHandle(
		ref,
		() => ({
			isNull: () => friendsRef.current.every(x => x === null),
			isError: () => refIsRequiredError(...Object.values(friendsRef.current)),
			getResult: () => {
				return friendsRef.current.reduce((acc, cur) => {
					let newAry = [...acc];
					if (cur) newAry.push(cur.getResult());
					return newAry;
				}, []);
			},
		}),
		// eslint-disable-next-line
		[]
	);

	return (
		<React.Fragment>
			<Grid container spacing={1}>
				{isFriends !== 0 && (
					<Grid item xs={12}>
						<Typography variant="h5" display="inlineBlock">
							友人邀約
						</Typography>
						<Button onClick={handleRemoveFriendsClick("all")}>清除全部</Button>
					</Grid>
				)}
				<TransitionGroup>
					{Array(isFriends)
						.fill()
						.map((x, idx) => {
							if (deleteIdx.indexOf(idx) !== -1) return null;
							return (
								<Collapse key={idx}>
									<Grid item xs={12} key={idx}>
										<GourpFriends ref={el => (friendsRef.current[idx] = el)} onRemoveFriendsClick={handleRemoveFriendsClick(idx)} options={options} />
									</Grid>
								</Collapse>
							);
						})}
				</TransitionGroup>
				<Grid item xs={12}>
					<Button onClick={handleAddFriendsClick} variant="outlined" startIcon={<Icons.Plus />}>
						{buttonLabel}
					</Button>
				</Grid>
			</Grid>
		</React.Fragment>
	);
});

export default InviteFriendsBox;
